import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useAppContext } from "../../app/AppContext";
import type { ProjectSummary } from "../../core/types";

/** What "Use this template" sends: exactly what projects.create takes (a name and the version). */
export type CreateFromTemplateInput = {
  name: string;
  templateVersionId: string;
};

/**
 * The canonical detail URL of a catalog template. The `scope=platform` query is inert (the
 * detail reads no scope any more) and stays so every detail link keeps the form it was
 * shared in.
 */
export function templateDetailPath(libraryKey: string, slug: string): string {
  return `/templates/${encodeURIComponent(libraryKey)}/${encodeURIComponent(slug)}?scope=platform`;
}

/**
 * The single "use template" command (the gallery detail): creates a FRESH project pinned to
 * the exact displayed version and opens it in the Builder. It never overwrites the current
 * project.
 */
export function useCreateFromTemplate() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { api } = useAppContext();
  return useMutation({
    mutationFn: (input: CreateFromTemplateInput) =>
      api.createProject({ name: input.name, templateVersionId: input.templateVersionId }),
    onSuccess: ({ project }) => {
      queryClient.setQueryData<{ projects: ProjectSummary[] }>(["projects"], (current) => ({
        projects: [
          project,
          ...(current?.projects ?? []).filter((candidate) => candidate.id !== project.id),
        ],
      }));
      navigate(`/projects/${encodeURIComponent(project.id)}`);
    },
  });
}
